import dayjs from "dayjs";
import axiosInstance from "../../services/axiosInstance";
import useMediaQueries from "../../hooks/useMediaQueries";
import EditDeleteMenu from "../edit_delete_menu/edit_delete_dot";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility"; // View icon
import DeleteIcon from "@mui/icons-material/Delete"; // Delete icon
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import UnsubscribeOutlinedIcon from "@mui/icons-material/UnsubscribeOutlined";
import WordViewerModal from "../word_view/word_viewer";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useModalStore } from "../../store/modalStore";
import { useNavigate } from "react-router-dom";
import { showLoader } from "../loader/show_loader";

import {
  Card,
  CardContent,
  Grid,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Box,
  Checkbox,
  FormControlLabel,
  Skeleton,
} from "@mui/material";

interface StoreCardProps {
  entryId: number;
  imageUrl: string;
  licenseGeneratedDate: string;
  leaseGeneratedDate: string;
  applicationGeneratedDate: string;
  markedReceivedDate: string;
  docsGeneratedBy: string;
  markedReceivedBy: string;

  storeName: string;
  storeCity: string;
  storeState: string;
  legalCorpName: string;
  onDelete: (entryId: number) => void; // Callback to remove the card after successful delete
}

const StoreCard: React.FC<StoreCardProps> = ({
  entryId,
  imageUrl,
  licenseGeneratedDate,
  leaseGeneratedDate,
  applicationGeneratedDate,
  markedReceivedDate,
  docsGeneratedBy,
  markedReceivedBy,
  storeName,
  storeCity,
  storeState,
  legalCorpName,
  onDelete, // Callback to handle the deletion of this store card
}) => {
  const { isMobile } = useMediaQueries();
  const navigate = useNavigate();
  const setActionsLoading = useModalStore((state) => state.setActionsLoading);

  const [loading, setLoading] = useState<boolean>(false); // State to handle loading
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false); // State to handle loading
  const [filesLoading, setFilesLoading] = useState<boolean>(false); // State to handle loading for files
  const [licensePdfGeneratedDate, setLicensePdfGeneratedDate] = useState<
    string | null
  >(licenseGeneratedDate);
  const [leasePdfGeneratedDate, setLeasePdfGeneratedDate] = useState<
    string | null
  >(leaseGeneratedDate);
  const [applicationPdfGeneratedDate, setAapplicationPdfGeneratedDate] =
    useState<string | null>(applicationGeneratedDate);
  const [emailReceived, setEmailReceived] = useState<boolean>(false); // State for email received
  const [receivedDate, setReceivedDate] = useState<string | null>(
    markedReceivedDate
  );

  // State to store the by's
  const [generatedBy, setGeneratedBy] = useState<string | null>(
    docsGeneratedBy
  );
  const [receivedby, setReceivedby] = useState<string | null>(markedReceivedBy);

  const [menuOpen, setMenuOpen] = useState<boolean>(false); // State to control menu visibility

  const [fileUrls, setFileUrls] = useState<any>([]); // Store file URLs
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [openPdfModal, setOpenPdfModal] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string>(""); // The URL of the Docs to open

  const [licenseChecked, setLicenseChecked] = React.useState(false);
  const [leaseChecked, setLeaseChecked] = React.useState(false);
  const [applicationChecked, setApplicationChecked] = React.useState(false);

  const handleLicenseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLicenseChecked(event.target.checked);
  };

  const handLeaseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLeaseChecked(event.target.checked);
  };

  const handApplicationChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setApplicationChecked(event.target.checked);
  };

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setFilesLoading(true);

    try {
      const res = await axiosInstance.get(`/api/v1/retail/get-docs/${entryId}`);
      setFileUrls(res.data.data);
    } catch (error) {
      toast.error("Error deleting file. Please try again.");
    } finally {
      setFilesLoading(false); // Set loading to false after the request completes
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenLicensePDF = () => {
    setPdfUrl(fileUrls?.licenseDoc); // Set the Word URL to open in the modal
    setOpenPdfModal(true); // Open the modal
    handleClose();
  };

  const handleOpenLeasePDF = () => {
    setPdfUrl(fileUrls?.leaseDoc); // Set the Word URL to open in the modal
    setOpenPdfModal(true); // Open the modal
    handleClose();
  };

  const handleOpenApplicationPDF = () => {
    setPdfUrl(fileUrls?.applicationDoc); // Set the Word URL to open in the modal
    setOpenPdfModal(true); // Open the modal
    handleClose();
  };

  useEffect(() => {
    setLicensePdfGeneratedDate(licenseGeneratedDate);
    setLeasePdfGeneratedDate(leaseGeneratedDate);
    setAapplicationPdfGeneratedDate(applicationGeneratedDate);
    setReceivedDate(markedReceivedDate);

    setGeneratedBy(docsGeneratedBy);
    setReceivedby(markedReceivedBy);
  }, [
    licenseGeneratedDate,
    leaseGeneratedDate,
    applicationGeneratedDate,
    markedReceivedDate,
    docsGeneratedBy,
    markedReceivedBy,
  ]);

  // Handle Docs Delete
  const handleDeletePDF = async () => {
    setActionsLoading(true);
    setDeleteLoading(true); // Set loading to true when starting the API request
    try {
      const response = await axiosInstance.delete(
        `/api/v1/retail/${entryId}/delete-docs`
      );
      const {
        licenseGeneratedDate,
        leaseGeneratedDate,
        applicationGeneratedDate,
        docsGeneratedBy,
      } = response.data.data; // Get the date from response
      setGeneratedBy(docsGeneratedBy);
      setLicensePdfGeneratedDate(licenseGeneratedDate);
      setLeasePdfGeneratedDate(leaseGeneratedDate);
      setAapplicationPdfGeneratedDate(applicationGeneratedDate);
      toast.success(`Docs deleted for ${storeName}`); // Show success toast
    } catch (error) {
      console.error("Error deleting Docs", error);
      toast.error("Error deleting Docs. Please try again.");
    } finally {
      setActionsLoading(false);
      setDeleteLoading(false); // Set loading to false after the request completes
    }
  };

  // Handle Docs Delete generation
  const handleGeneratePDF = async () => {
    setActionsLoading(true);
    setLoading(true); // Set loading to true when starting the API request
    try {
      const response = await axiosInstance.post(
        `/api/v1/retail/gen-docs/${entryId}`,
        {
          licenseDocx: licenseChecked,
          leaseDocx: leaseChecked,
          applicationDocx: applicationChecked,
          method: "docx",
        }
      );
      const {
        licenseGeneratedDate,
        leaseGeneratedDate,
        applicationGeneratedDate,
        docsGeneratedBy,
      } = response.data.data; // Get the date from response
      setGeneratedBy(docsGeneratedBy);
      setLicensePdfGeneratedDate(licenseGeneratedDate); // Store the generated date
      setLeasePdfGeneratedDate(leaseGeneratedDate); // Store the generated date
      setAapplicationPdfGeneratedDate(applicationGeneratedDate); // Store the generated date
      setLeaseChecked(false);
      setLicenseChecked(false);
      setApplicationChecked(false);
      toast.success(`Docs generated for ${storeName}`); // Show success toast
    } catch (error) {
      console.error("Error generating Docs", error);
      toast.error("Error generating Docs. Please try again.");
    } finally {
      setActionsLoading(false);
      setLoading(false); // Set loading to false after the request completes
    }
  };

  // Handle marking email as received
  const handleEmailReceived = async () => {
    setActionsLoading(true);
    setEmailReceived(true); // Set email received state to true (indicating the request is being processed)
    try {
      const response = await axiosInstance.post(
        `/api/v1/retail/${entryId}/mark-email-received`
      );
      const { markedReceivedDate, markedReceivedBy } = response.data.data; // Get the date from response
      setReceivedby(markedReceivedBy);
      setReceivedDate(markedReceivedDate);
      toast.success(`Email marked as received for ${storeName}`); // Show success toast
    } catch (error) {
      console.error("Error marking email as received", error);
      toast.error("Error marking email as received. Please try again.");
    } finally {
      setActionsLoading(false);
      setEmailReceived(false); // Reset email received state after the request completes
    }
  };

  // Handle deleting the retail entry
  const handleDeleteEntry = async () => {
    if (
      window.confirm(
        `Are you sure you want to delete the store entry for ${storeName}?`
      )
    ) {
      try {
        setActionsLoading(true);
        setDeleteLoading(true); // Set loading to true when starting the delete request
        await axiosInstance.delete(`/api/v1/retail/${entryId}`); // Send the delete request

        toast.success(`Retail entry for ${storeName} deleted successfully.`); // Show success toast
        onDelete(entryId); // Callback to remove the entry from the UI
        setMenuOpen(false); // Close the menu after successful deletion
      } catch (error) {
        console.error("Error deleting entry", error);
        toast.error("Error deleting the retail entry. Please try again.");
      } finally {
        setActionsLoading(false);
        setDeleteLoading(false); // Reset loading state after the request completes
      }
    }
  };

  return (
    <Card
      sx={{
        display: "flex",
        width: "100%",
        mt: 3,
        borderRadius: 2,
        boxShadow: "inset rgba(0, 0, 0, 0.1) 0px 0px 3px 0px",
      }}
    >
      {showLoader([deleteLoading])}
      {/* Left Grid: Icon */}
      {!isMobile && (
        <Grid
          item
          xs={4}
          p={1}
          container
          justifyContent="center"
          alignItems="center"
        >
          <IconButton sx={{ borderRadius: 5, width: "100%", height: "100%" }}>
            {receivedDate ? (
              <MarkEmailReadOutlinedIcon
                sx={{ fontSize: 100, color: "#355E3B" }}
              />
            ) : (
              <UnsubscribeOutlinedIcon sx={{ fontSize: 100, color: "red" }} />
            )}
          </IconButton>
        </Grid>
      )}

      {/* Right Grid: Store Info and Buttons */}
      <Grid
        item
        xs={8}
        sx={{
          display: "flex",
          flexDirection: !isMobile ? "column" : "row",
          justifyContent: "space-around",
          height: "100%",
          padding: 1,
          paddingBottom: 2,
          width: "100%",
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
            {storeName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {storeCity}, {storeState} - {legalCorpName}
          </Typography>
        </CardContent>

        {/* Buttons at the bottom */}
        <Grid
          container
          item
          // spacing={1}
          sx={{
            marginTop: "auto",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
          xs={12}
        >
          <Grid item mx={1} xs={isMobile ? 12 : 4}>
            {!Boolean(receivedDate) && (
              <Box
                sx={
                  {
                    // borderRadius: 2,
                    // boxShadow: `inset rgba(0, 0, 0, 0.1) 0px 0px 3px 0px`,
                    // // padding: 1,
                  }
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    // gap: 2,
                    justifyContent: "space-evenly",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={licenseChecked}
                        onChange={handleLicenseChange}
                        disabled={loading}
                      />
                    }
                    label="License"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={leaseChecked}
                        onChange={handLeaseChange}
                        disabled={loading}
                      />
                    }
                    label="Sign Face"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={applicationChecked}
                        onChange={handApplicationChange}
                        disabled={loading}
                      />
                    }
                    label="Application"
                  />
                </Box>
                <Button
                  fullWidth={true} // Change this to false to prevent button from taking full width
                  size="medium"
                  variant="outlined"
                  color="primary"
                  onClick={handleGeneratePDF}
                  disabled={
                    loading ||
                    !(licenseChecked || leaseChecked || applicationChecked)
                  }
                  endIcon={
                    loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <PictureAsPdfOutlinedIcon />
                    )
                  }
                  sx={{ borderRadius: 5 }}
                >
                  {loading ? "Generating..." : "Generate DOCs"}
                </Button>
              </Box>
            )}
          </Grid>
          <Grid item mx={1} xs={isMobile ? 12 : 2}>
            {(licensePdfGeneratedDate ||
              leasePdfGeneratedDate ||
              applicationPdfGeneratedDate) && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                sx={{ width: "100%" }}
              >
                <Tooltip title={`Generated by ${generatedBy}`} arrow>
                  <Typography
                    variant="body2"
                    color="success"
                    sx={{ cursor: "help" }}
                  >
                    DOCs generated
                  </Typography>
                </Tooltip>

                {/* Keep the View and Delete links on the same line */}
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={0.5}
                >
                  <Tooltip title={"Open list of files that are generated"}>
                    <Button
                      size="small"
                      // component={Button}
                      // fontSize={"12px"}
                      onClick={handleClick}
                      startIcon={<VisibilityIcon />}
                      sx={{
                        borderRadius: 10,
                      }}
                      color="info"
                    >
                      View
                    </Button>
                  </Tooltip>

                  <Tooltip
                    title={
                      Boolean(receivedDate)
                        ? "Once the Application is marked as Received, documents can neither be generated nor be deleted"
                        : ""
                    }
                  >
                    <span>
                      <Button
                        disabled={Boolean(receivedDate)}
                        size="small"
                        // component={Button}
                        // fontSize={"12px"}
                        onClick={handleDeletePDF}
                        startIcon={<DeleteIcon />}
                        sx={{
                          borderRadius: 10,
                        }}
                        color="error"
                      >
                        Delete
                      </Button>
                    </span>
                  </Tooltip>
                </Box>

                {fileUrls && (
                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "basic-button",
                    }}
                  >
                    {filesLoading ? (
                      <Skeleton variant="rectangular" height={60} width={120} />
                    ) : (
                      <Box>
                        {fileUrls.licenseDoc && (
                          <Tooltip
                            title={`Generated on ${dayjs(licensePdfGeneratedDate).format("DD/MM/YYYY HH:mm:ss")}`}
                            arrow
                          >
                            <MenuItem onClick={handleOpenLicensePDF}>
                              License DOC
                            </MenuItem>
                          </Tooltip>
                        )}
                        {fileUrls.leaseDoc && (
                          <Tooltip
                            title={`Generated on ${dayjs(leasePdfGeneratedDate).format("DD/MM/YYYY HH:mm:ss")}`}
                            arrow
                          >
                            <MenuItem onClick={handleOpenLeasePDF}>
                              Sign Face DOC
                            </MenuItem>
                          </Tooltip>
                        )}
                        {fileUrls.applicationDoc && (
                          <Tooltip
                            title={`Generated on ${dayjs(leasePdfGeneratedDate).format("DD/MM/YYYY HH:mm:ss")}`}
                            arrow
                          >
                            <MenuItem onClick={handleOpenApplicationPDF}>
                              Application DOC
                            </MenuItem>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Menu>
                )}
              </Box>
            )}
          </Grid>
          <Grid item mx={1} xs={isMobile ? 12 : 4}>
            <>
              {receivedDate ? (
                <Tooltip title={`Marked by ${receivedby}`} arrow>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ cursor: "help" }}
                    textAlign={"right"}
                  >
                    Marked as Email Received on{" "}
                    {dayjs(receivedDate).format("DD/MM/YYYY HH:mm:ss")}
                  </Typography>
                </Tooltip>
              ) : (
                <Button
                  fullWidth={true}
                  size="medium"
                  variant="outlined"
                  color="success"
                  onClick={handleEmailReceived}
                  disabled={emailReceived}
                  endIcon={
                    emailReceived ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <CheckCircleOutlineIcon />
                    )
                  }
                  sx={{ borderRadius: 5 }}
                >
                  {emailReceived ? "Processing..." : "Email Received"}
                </Button>
              )}
            </>
          </Grid>
        </Grid>
      </Grid>

      <WordViewerModal
        wordUrl={pdfUrl} // Assuming pdfUrl is now the URL of the Word document
        open={openPdfModal}
        onClose={() => setOpenPdfModal(false)}
      />

      <Grid item mx={1} my={2} xs={0.5} container justifyContent={"flex-end"}>
        <EditDeleteMenu
          hideEditOption={Boolean(receivedDate)}
          hideDeleteOption={false}
          hideViewOption={false}
          handleEditMenuClick={() => navigate(`/edit-form/${entryId}`)}
          handleDeleteMenuClick={handleDeleteEntry} // Integrating delete functionality
          handleViewMenuClick={() => navigate(`/view-form/${entryId}`)}
          onClose={() => setMenuOpen(false)} // Close the menu when done
        />
      </Grid>
    </Card>
  );
};

export default StoreCard;
